<script setup>
/**
 * ToBox: the gift send modal's one smart To box (2026-10-22, Chris; design
 * `gift-flow-review-2026-10-22/DESIGN.md` §3, mockups S1 to S4).
 *
 * Empty: "Name from your circle, email or phone". On focus the dropdown lists
 * the circle (Coven first) with its footer, "Not on Mystic Aida? Type their
 * email or mobile number." (field help lives in the dropdown, never as a
 * caption that stays). "@" turns the rows into domain completions (the first
 * highlighted, `tab`); digits format as a phone with one "Text …" row
 * (`enter`); a typo in a common domain shows "Did you mean …? [Use it]" under
 * the box (feedback, always shown). A picked row becomes ONE token with its ✕:
 * a gift is one-to-one.
 *
 * v-model:token ({kind, label, uuid, value} | null) and v-model:text (what is
 * typed, so the modal's button can follow a whole address before it is
 * tokenised).
 */
import { ref, computed, watch, nextTick } from 'vue'
import { toSuggestions, toTokenFor, domainHint, contactKind, formatPhone, isPhoneish } from '@/utils/gift-send'

const props = defineProps({
  token: { type: Object, default: null },
  text: { type: String, default: '' },
  friends: { type: Array, default: () => [] },
  placeholder: { type: String, default: 'Name from your circle, email or phone' },
  disabled: { type: Boolean, default: false },
})
const emit = defineEmits(['update:token', 'update:text', 'picked', 'cleared'])

const input = ref(null)
const focused = ref(false)
const active = ref(0)
const suggestions = computed(() => toSuggestions(props.text, props.friends))
// A typo in a common domain: "Did you mean…?" under the box says it, not a row of the same address.
const rows = computed(() => (domainHint(props.text) ? [] : suggestions.value.rows))
const open = computed(() => focused.value && !props.token && (rows.value.length > 0 || !!suggestions.value.footer))
const typo = computed(() => (props.token ? '' : domainHint(props.text)))
watch(() => props.text, () => { active.value = 0 })

function onInput (e) {
  const v = e.target.value
  // Digits format as a phone while typing; anything else as typed.
  const next = isPhoneish(v) && !v.includes('@') && /\d$/.test(v) ? formatPhone(v) : v
  if (next !== v) e.target.value = next
  emit('update:text', next)
}
function pick (row) {
  if (!row) return
  const token = toTokenFor(row)
  emit('update:token', token)
  emit('update:text', '')
  emit('picked', token)
}
function useTypo () { pick({ kind: 'email', value: typo.value }) }
/** A whole address or number typed and left: it becomes the token. */
function commitTyped () {
  if (domainHint(props.text)) return   // "Did you mean…?" stays until they choose
  const kind = contactKind(props.text)
  if (kind) pick({ kind, value: props.text })
}
function clear () {
  emit('update:token', null)
  emit('cleared')
  nextTick(() => { try { input.value && input.value.focus() } catch (e) { /* */ } })
}
function onKey (e) {
  if (e.key === 'ArrowDown' && rows.value.length) { e.preventDefault(); active.value = (active.value + 1) % rows.value.length; return }
  if (e.key === 'ArrowUp' && rows.value.length) { e.preventDefault(); active.value = (active.value - 1 + rows.value.length) % rows.value.length; return }
  if ((e.key === 'Enter' || (e.key === 'Tab' && props.text)) && open.value && rows.value[active.value]) {
    e.preventDefault(); pick(rows.value[active.value]); return
  }
  if (e.key === 'Enter') { e.preventDefault(); commitTyped(); return }
  if (e.key === 'Escape' && open.value) { e.stopPropagation(); focused.value = false; return }
  if (e.key === 'Backspace' && !props.text && props.token) clear()
}
function onBlur () { focused.value = false; commitTyped() }
function focus () { try { input.value && input.value.focus() } catch (e) { /* */ } }
defineExpose({ focus })
</script>

<template>
  <div class="tb" :class="{ focused: focused && !token, 'has-token': !!token }" data-testid="to-box">
    <label class="tb-box" @click="!token && focus()">
      <span class="tb-to">To</span>
      <span v-if="token" class="tb-token" :class="'tb-' + token.kind" data-testid="to-token" :data-kind="token.kind">
        <span v-if="token.kind === 'member'" class="tb-ava" aria-hidden="true">{{ token.label.charAt(0).toUpperCase() }}</span>
        <span v-else class="tb-ico" aria-hidden="true"><i :class="['fas', token.kind === 'phone' ? 'fa-comment' : 'fa-envelope']"></i></span>
        <span class="tb-label">{{ token.label }}</span>
        <em v-if="token.kind === 'member'" class="tb-tag">Messages</em>
        <button v-if="!disabled" type="button" class="tb-x" :aria-label="`Remove ${token.label}`" data-testid="to-token-x" @click.stop="clear">
          <i class="fas fa-times" aria-hidden="true"></i>
        </button>
      </span>
      <input v-else ref="input" class="tb-input" data-testid="to-input" type="text" :value="text" :placeholder="placeholder"
             :disabled="disabled" autocomplete="off" autocapitalize="off" spellcheck="false" inputmode="email"
             role="combobox" aria-autocomplete="list" aria-controls="tb-list" :aria-expanded="open ? 'true' : 'false'"
             aria-label="To: a name from your circle, an email or a phone number"
             @input="onInput" @focus="focused = true" @blur="onBlur" @keydown="onKey" />
    </label>

    <div v-if="open" class="tb-drop" data-testid="to-drop">
      <p v-if="suggestions.kind === 'circle' && rows.length" class="tb-head">Your circle</p>
      <ul id="tb-list" class="tb-list" role="listbox">
        <li v-for="(r, i) in rows" :key="r.key" role="option" :aria-selected="i === active ? 'true' : 'false'"
            :class="['tb-row', 'tb-row-' + r.kind, { on: i === active }]" :data-testid="'to-row-' + r.kind"
            @mousedown.prevent="pick(r)" @mouseenter="active = i">
          <template v-if="r.kind === 'member'">
            <span class="tb-ava" aria-hidden="true">{{ r.name.charAt(0).toUpperCase() }}</span>
            <span class="tb-who"><b>{{ r.name }}</b><small>{{ r.sub }}</small></span>
            <em v-if="r.coven" class="tb-coven">Coven</em>
          </template>
          <template v-else-if="r.kind === 'email'">
            <span class="tb-ico" aria-hidden="true"><i class="fas fa-envelope"></i></span>
            <span class="tb-who"><b class="tb-addr">{{ r.local }}<span class="tb-dom">{{ r.domain }}</span></b></span>
            <kbd v-if="i === active" class="tb-kbd">tab</kbd>
          </template>
          <template v-else>
            <span class="tb-ico" aria-hidden="true"><i class="fas fa-comment"></i></span>
            <span class="tb-who"><b>{{ r.label }}</b><small>{{ r.sub }}</small></span>
            <kbd class="tb-kbd">enter</kbd>
          </template>
        </li>
      </ul>
      <p v-if="suggestions.footer" class="tb-foot" data-testid="to-foot">{{ suggestions.footer }}</p>
    </div>

    <p v-if="typo" class="tb-typo" role="status" data-testid="to-typo">
      <i class="fas fa-wand-magic-sparkles" aria-hidden="true"></i>
      <span>Did you mean <b>{{ typo }}</b>?</span>
      <button type="button" class="tb-use" data-testid="to-typo-use" @mousedown.prevent @click="useTypo">Use it</button>
    </p>
  </div>
</template>

<style scoped>
.tb { position: relative; --ink: #2a1845; --muted: #6b5a82; --amethyst: #4e2776; --violet: #7b4bb0; --gold-dk: #8a6a2a;
  --ui: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; --display: 'Letter Display', 'Cinzel', Georgia, serif; }
.tb-box {
  display: flex; align-items: center; gap: 12px; min-height: 52px; box-sizing: border-box; padding: 6px 8px 6px 16px;
  border-radius: 16px; border: 1.5px solid #ddd0ef; background: #fff; cursor: text;
}
.tb.focused .tb-box { border-color: var(--violet); box-shadow: 0 0 0 4px rgba(123, 75, 176, 0.14); }
.tb-to { flex: none; font: 600 11px var(--display); letter-spacing: 0.16em; text-transform: uppercase; color: var(--muted); }
/* 16px: anything smaller and iOS Safari zooms the whole sheet on focus. */
.tb-input { flex: 1 1 auto; min-width: 0; min-height: 44px; border: 0; outline: none; background: transparent; padding: 0;
  color: var(--ink); font: 500 16px var(--ui); }
.tb-input::placeholder { color: #a596b8; }
.tb-token { display: inline-flex; align-items: center; gap: 8px; min-width: 0; max-width: 100%; padding: 4px 4px 4px 4px;
  border-radius: 999px; background: #f1ebfa; border: 1px solid #ddd0ef; }
.tb-token .tb-label { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font: 600 15px var(--ui); color: var(--ink); }
.tb-token.tb-member { padding-left: 4px; }
.tb-ava { flex: none; width: 32px; height: 32px; border-radius: 50%; display: grid; place-items: center; background: #efe5d2;
  color: var(--amethyst); font: 700 13px var(--ui); }
.tb-ico { flex: none; width: 32px; height: 32px; border-radius: 50%; display: grid; place-items: center; background: #ece2f8; color: var(--violet); font-size: 13px; }
.tb-token.tb-phone .tb-ico { background: var(--amethyst); color: #f3d98f; }
.tb-tag { flex: none; font: 600 9.5px var(--display); font-style: normal; letter-spacing: 0.16em; text-transform: uppercase; color: var(--gold-dk); }
.tb-x { position: relative; flex: none; width: 32px; height: 32px; margin-left: -2px; border: 0; border-radius: 50%; background: none; cursor: pointer;
  color: #8a78ad; font-size: 12px; display: grid; place-items: center; }
/* A 44px hit area round the 32px ✕ (e2e phone walk 2026-10-30). */
.tb-x::after { content: ''; position: absolute; inset: -6px; border-radius: 50%; }
.tb-x:hover { color: var(--amethyst); background: #e6dcf4; }
/* On touch the ✕ itself is 44px (e2e round 2): drawn at 32, the chip's size unchanged. */
@media (pointer: coarse) {
  .tb-x { width: 44px; height: 44px; margin: -6px -6px -6px -8px; padding: 6px; box-sizing: border-box; background-clip: content-box; }
  .tb-x::after { content: none; }
}

.tb-drop { position: absolute; left: 0; right: 0; top: calc(100% + 8px); z-index: 40; padding: 8px; box-sizing: border-box;
  background: #fff; border: 1px solid #e3d4f5; border-radius: 18px; box-shadow: 0 20px 40px -18px rgba(42, 24, 69, 0.5);
  max-height: min(380px, 52vh); overflow-y: auto; }
.tb-head { margin: 6px 10px 6px; font: 600 10.5px var(--display); letter-spacing: 0.2em; text-transform: uppercase; color: var(--gold-dk); }
.tb-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 2px; }
.tb-row { display: flex; align-items: center; gap: 12px; min-height: 52px; padding: 6px 10px; border-radius: 12px; cursor: pointer; }
.tb-row.on { background: #f1ebfa; }
.tb-who { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; }
.tb-who b { font: 600 15px/1.25 var(--ui); color: var(--ink); overflow-wrap: anywhere; }
.tb-who small { font: 500 12.5px/1.35 var(--ui); color: var(--muted); }
.tb-dom { color: var(--violet); }
.tb-coven { flex: none; font: 600 10px var(--display); font-style: normal; letter-spacing: 0.16em; text-transform: uppercase; color: var(--gold-dk); }
.tb-kbd { flex: none; padding: 2px 7px; border-radius: 7px; border: 1px solid #d9c9ef; background: #faf7fe; color: #8a78ad; font: 500 11px var(--ui); }
.tb-foot { margin: 6px 4px 2px; padding: 10px 8px 4px; border-top: 1px solid #ece4f6; font: 500 12.5px/1.45 var(--ui); color: #8a7a9f; }

.tb-typo { display: flex; align-items: center; gap: 10px; margin: 10px 0 0; padding: 8px 8px 8px 14px; border-radius: 14px;
  background: #fff6e2; border: 1px solid #e7c983; color: var(--ink); font: 500 14px/1.4 var(--ui); }
.tb-typo > i { flex: none; color: #b8902f; }
.tb-typo > span { flex: 1 1 auto; min-width: 0; overflow-wrap: anywhere; }
.tb-use { flex: none; min-height: 44px; padding: 0 16px; border-radius: 999px; border: 0; background: var(--amethyst); color: #fff;
  font: 700 13.5px var(--ui); cursor: pointer; }
.tb-x:focus-visible, .tb-use:focus-visible { outline: 2px solid var(--violet); outline-offset: 2px; }
</style>
